import {
    Breadcrumb,
    Flex,
    Header,
    Label,
    useStyles,
    useTheme,
    useAppDisplayModeContext,
} from '@archipro-design/aria';

import type { FilterStates } from '~/modules/magazine/type/filter-states';
import { FilterSection } from '../filter-section/FilterSection';
import { SubscribePublicationsTrigger } from '@modules-rocco/subscription/component/SubscribePublicationsTrigger';
import NewsletterSubscriptionForm from '../newsletter-subscription-form/NewsletterSubscriptionForm';
import { useFeature } from '~/modules/root';

import * as S from './HeaderSection.style';
import { TitleSectionMobile } from '~/modules/category/component/title-section/TitleSection.mobile';
import { Link } from 'react-router';
import type { MetaBreadcrumb } from '~/modules/category/type';
import type { digitalPublicationFilters } from '../../api/digital-publication-filters.server';

interface HeaderSectionProps {
    description?: string;
    breadcrumb?: MetaBreadcrumb[];
    title: string;
    itemsCount: number;
    initialFilterState: FilterStates;
    filters: Awaited<ReturnType<typeof digitalPublicationFilters>>;
}

export const HeaderSection = (props: HeaderSectionProps) => {
    const {
        description,
        breadcrumb,
        title,
        itemsCount,
        initialFilterState,
        filters,
    } = props;

    const theme = useTheme();
    const { desktop } = useAppDisplayModeContext();
    const canChoosePublications = useFeature(
        'digital_publication_subscribe_2026',
    );

    const { css } = useStyles();

    if (!desktop) {
        return (
            <TitleSectionMobile title={title} subtitle={String(itemsCount)}>
                <FilterSection
                    initialStates={initialFilterState}
                    filters={filters}
                />
            </TitleSectionMobile>
        );
    }

    return (
        <div className={css(S.PageTopSectionWrapper)}>
            <div className={css(S.BreadcrumbWrapper)}>
                <Breadcrumb
                    logo={null}
                    home={{
                        link: true,
                        as: Link,
                        label: 'Home',
                        to: '/',
                    }}
                    items={breadcrumb ?? []}
                    variables={{
                        breadcrumbLogoColor:
                            theme.siteVariables.colorScheme?.default
                                ?.foreground,
                    }}
                />
            </div>

            <Flex space="between">
                <div className={css(S.TitleWrapper)}>
                    <Header inline maximumLines={2}>
                        {title}
                    </Header>
                    <Label inline className={css(S.CategoryCountWrapper)}>
                        {itemsCount?.toLocaleString()}
                    </Label>
                </div>
                <div className={css(S.CategoryDescriptionWrapper)}>
                    <Label
                        variant="02"
                        dangerouslySetInnerHTML={{
                            __html: description ?? '',
                        }}
                    />
                    {canChoosePublications ? (
                        <SubscribePublicationsTrigger className="mt-5 mb-1" />
                    ) : (
                        <NewsletterSubscriptionForm />
                    )}
                </div>
            </Flex>
        </div>
    );
};
